import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * "Added to cart" confirmation modal with often-bought-together recommendations
 * (snippets/curaprox-atc-modal.liquid).
 *
 * Opens when the PDP's product form dispatches its CartAddEvent (`cart:update`
 * with source `product-form-component`). The productId filter keeps quick-adds
 * fired from the recommendation cards inside the modal (they carry the
 * recommended product's id) from re-triggering it.
 *
 * @extends {DialogComponent}
 */
class CuraproxAtcModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);
  }

  /**
   * Patches the server-rendered product row (name / option / thumbnail) to the
   * variant that was actually added. The markup is rendered once with the
   * page-load variant baked in and is NOT part of the variant-picker morph, so
   * after a client-side variant switch it showed the stale variant while the
   * cart received the right one. The per-variant data comes from the JSON map
   * the snippet renders (`script[data-atc-modal-variants]`); single-variant
   * products render no map and skip out here. A lookup miss (e.g. an event
   * whose sourceId is not a variant id) keeps the server-rendered row.
   *
   * @param {string | undefined} variantId - The added variant's id
   */
  updateForVariant(variantId) {
    const id = String(variantId ?? '');
    if (!id || id === this.dataset.currentVariantId) return;

    const mapElement = this.querySelector('script[data-atc-modal-variants]');
    if (!mapElement) return;

    /** @type {Record<string, {name?: string, variant?: string, image?: string|null}>} */
    let variants;
    try {
      variants = JSON.parse(mapElement.textContent || '{}');
    } catch (error) {
      return;
    }

    const entry = variants[id];
    if (!entry) return;

    const name = this.querySelector('.atc-modal__name');
    const variant = this.querySelector('.atc-modal__variant');
    const image = this.querySelector('.atc-modal__media-img');
    if (name && entry.name) name.textContent = entry.name;
    if (variant && entry.variant) variant.textContent = entry.variant;
    if (image instanceof HTMLImageElement && entry.image) image.src = entry.image;

    this.dataset.currentVariantId = id;
  }

  /**
   * @param {import('@theme/events').CartAddEvent} event
   */
  #onCartAdd = (event) => {
    const data = event.detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;
    if (String(data.productId) !== this.dataset.productId) return;

    // On a successful add, product-form.js passes the submitted variant id as
    // the event's sourceId (`new CartAddEvent(undefined, id.toString(), ...)`).
    this.updateForVariant(event.detail?.sourceId);
    this.showDialog();
  };
}

if (!customElements.get('curaprox-atc-modal')) {
  customElements.define('curaprox-atc-modal', CuraproxAtcModal);
}

const ATC_MODAL_SECTION_ID = 'section-rendering-atc-modal';

/**
 * Brings the SAME confirmation modal to pages that don't server-render one
 * (snippets/curaprox-atc-modal-host.liquid, rendered globally in layout/theme.liquid).
 *
 * The modal is per-product markup — image, title and the PIM-driven "Often bought
 * together" cards all come from `product` — so it only exists on the PDP. Adding
 * from a collection/search listing (the round quick-add, or "Add to cart" inside
 * the quick-add modal) previously showed nothing but a cart-badge bump
 * (client 2026-07-29). On a successful add with no matching modal already in the
 * page, this fetches that product's modal through the Section Rendering API and
 * opens it.
 *
 * Fetched HTML is cached per product id, so re-adding the same product is instant
 * and never re-fetches.
 */
class CuraproxAtcModalHost extends HTMLElement {
  /** @type {Map<string, string>} productId → fetched modal markup */
  #cache = new Map();
  #pending = false;

  /** @type {Map<string, string>} product URL → fetched modal markup (warmed on hover) */
  #urlCache = new Map();

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);
    // Warm the modal while the shopper is still deciding. Measured cold, an add
    // from a listing took ~1.2s for /cart/add plus ~0.6s to fetch this modal, and
    // that trailing delay reads as "nothing happened" — shoppers click again and
    // add a second unit. Hovering a card is a reliable predictor of a click, and
    // the fetch is idempotent and cached, so at worst it is one wasted request.
    document.addEventListener('pointerenter', this.#prefetchOnHover, { capture: true, passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);
    document.removeEventListener('pointerenter', this.#prefetchOnHover, { capture: true });
  }

  /**
   * @param {PointerEvent} event
   */
  #prefetchOnHover = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('product-card, .rvp-card, .obt-card');
    if (!card) return;

    const link = card.querySelector('a[href*="/products/"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    this.#warm(link.href);
  };

  /**
   * Fetches and caches a product's modal markup, once per URL.
   * @param {string} productUrl - The product URL to warm.
   * @returns {Promise<string|null>} The cached markup.
   */
  async #warm(productUrl) {
    const key = productUrl.split('?')[0];
    if (this.#urlCache.has(key)) return this.#urlCache.get(key) ?? null;
    // Reserve the slot immediately so concurrent hovers don't stack requests.
    this.#urlCache.set(key, '');

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', ATC_MODAL_SECTION_ID);
      const response = await fetch(url.toString());
      if (!response.ok) return null;

      const markup = await response.text();
      if (!markup.includes('curaprox-atc-modal')) return null;

      this.#urlCache.set(key, markup);
      return markup;
    } catch (error) {
      this.#urlCache.delete(key);
      return null;
    }
  }

  /**
   * @param {import('@theme/events').CartAddEvent} event
   */
  #onCartAdd = async (event) => {
    const data = event.detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;

    const productId = String(data.productId ?? '');
    if (!productId) return;

    // A modal for this product is already on the page (the PDP copy, or one this
    // host injected earlier) — its own listener opens it, so stay out of the way.
    if (document.querySelector(`curaprox-atc-modal[data-product-id="${CSS.escape(productId)}"]`)) return;

    // An add fired while a confirmation modal is OPEN comes from the "Often bought
    // together" cards inside it. The PDP copy ignores those via its productId
    // filter; without this the host would stack a second modal on top of the first.
    if (document.querySelector('curaprox-atc-modal dialog[open]')) return;

    if (this.#pending) return;
    this.#pending = true;

    try {
      const html = this.#cache.get(productId) ?? (await this.#fetchModal(productId, event));
      if (!html) return;

      this.#cache.set(productId, html);
      this.innerHTML = html;

      const modal = this.querySelector('curaprox-atc-modal');
      if (!(modal instanceof CuraproxAtcModal)) return;

      // The fetched markup renders the product's first-available variant, but a
      // card swatch / quick-add may have added a different one — patch the row
      // to the variant the event actually added (sourceId, see product-form.js).
      modal.updateForVariant(event.detail?.sourceId);

      // Let the upgraded element resolve its `ref="dialog"` before showing it.
      await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
      modal.showDialog();
    } catch (error) {
      // Never break the add-to-cart flow over a missing confirmation modal.
      console.error('[atc-modal] could not render the confirmation modal', error);
    } finally {
      this.#pending = false;
    }
  };

  /**
   * Fetches the modal markup for a product.
   * @param {string} productId - The added product's id
   * @param {import('@theme/events').CartAddEvent} event - The originating event
   * @returns {Promise<string|null>} The modal markup, or null when it can't be resolved
   */
  async #fetchModal(productId, event) {
    const productUrl = await this.#resolveProductUrl(productId, event);
    if (!productUrl) return null;

    // Hovering the card usually warmed this already, so the modal opens as soon
    // as the cart responds instead of a further ~600ms later.
    const warmed = this.#urlCache.get(productUrl.split('?')[0]);
    if (warmed) return warmed;

    return this.#warm(productUrl);
  }

  /**
   * Resolves the added product's URL.
   *
   * When the event carries the updated cart as `detail.resource`, its line items
   * expose `product_id` + `url`. Since `product-form.js` stopped waiting for the
   * cart before dispatching (the modal used to open a round trip late), that is
   * no longer the usual case, so the dispatching form's own `data-product-url`
   * comes next — it covers the quick-add dialog too, whose form sits outside any
   * product card — and the card link is the remaining fallback.
   *
   * @param {string} productId - The added product's id
   * @param {import('@theme/events').CartAddEvent} event - The originating event
   * @returns {Promise<string|null>} The product URL
   */
  async #resolveProductUrl(productId, event) {
    const items = event.detail?.resource?.items;
    if (Array.isArray(items)) {
      const item = items.find((entry) => String(entry?.product_id) === productId);
      if (item?.url) return item.url;
      // `Theme.routes` has no root url (snippets/scripts.liquid), so the locale-aware
      // root comes from the host element's own data attribute.
      if (item?.handle) return `${this.dataset.rootUrl || '/'}products/${item.handle}`.replace(/\/\/products/, '/products');
    }

    const target = event.target;
    if (target instanceof Element) {
      // The dispatching form may know its own product URL (`data-product-url` in
      // blocks/buy-buttons.liquid). Cards rendered client-side by the
      // Meilisearch collection/search renderers don't carry it, hence the DOM
      // walk below.
      const form = target.closest('product-form-component');
      if (form instanceof HTMLElement && form.dataset.productUrl) return form.dataset.productUrl;

      // Climb through the wrappers looking for the card's own product link.
      // `closest()` stops at the NEAREST match, which on a listing card is
      // `<quick-add-component>` — a wrapper around the button that contains no
      // link — so a single lookup gave up before reaching `<product-card>`.
      let node = /** @type {Element | null} */ (target);
      while (node) {
        const scope = node.closest('li, .product-grid__item, .card-gallery, quick-add-component, product-card');
        if (!scope) break;

        const link = scope.querySelector('a[href*="/products/"]');
        if (link instanceof HTMLAnchorElement) return link.href;

        node = scope.parentElement;
      }
    }

    return this.#resolveProductUrlFromCart(productId);
  }

  /**
   * Last resort: read the cart and match the added product's line item. Reached
   * only when the add carries neither the cart nor a product link in the DOM,
   * and it costs one small request on a flow that is about to fetch the modal
   * markup anyway.
   *
   * @param {string} productId - The added product's id
   * @returns {Promise<string|null>} The product URL
   */
  async #resolveProductUrlFromCart(productId) {
    try {
      const response = await fetch(`${Theme.routes.cart_url || '/cart'}.js`);
      if (!response.ok) return null;

      const cart = await response.json();
      const item = cart?.items?.find((entry) => String(entry?.product_id) === productId);
      if (item?.url) return item.url;
      if (item?.handle) return `${this.dataset.rootUrl || '/'}products/${item.handle}`.replace(/\/\/products/, '/products');
    } catch (error) {
      return null;
    }

    return null;
  }
}

if (!customElements.get('curaprox-atc-modal-host')) {
  customElements.define('curaprox-atc-modal-host', CuraproxAtcModalHost);
}
